import CustomScrollbar from '@/components/CommonComponents/CustomScrollbar';
import QuestionRenderer from '@/components/CommonComponents/QuestionRenderer';
import { SurveyListItem } from '@/types/survey.types';
import { ChevronLeft, ChevronRight, FileText, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface SurveyPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  survey: SurveyListItem | null;
}

const SurveyPreviewModal: React.FC<SurveyPreviewModalProps> = ({ isOpen, onClose, survey }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [currentLanguageIndex, setCurrentLanguageIndex] = useState(0);

  const surveyLanguages = useMemo(() => {
    if (!survey?.questionsJson?.length) return [];

    const firstQuestion = survey.questionsJson[0];

    return Object.keys(firstQuestion)
      .filter((key) => key.startsWith('label::'))
      .map((key) => {
        const lang = key.replace('label::', '').trim();
        const match = lang.match(/(.+)\((.+)\)/);

        return {
          value: lang,
          name: match?.[1]?.trim() ?? lang,
          code: match?.[2]?.trim() ?? '',
        };
      });
  }, [survey]);

  // Auto-select default language or first language when modal opens
  useMemo(() => {
    if (!isOpen) {
      setSelectedLanguage('');
    } else if (surveyLanguages.length > 0 && !selectedLanguage) {
      const defaultLang = survey?.defaultLanguageId
        ? surveyLanguages.find((lang) => {
            // Find language by matching the defaultLanguageId with the language id from the languages array
            const matchingLanguage = survey.languages?.find(
              (l) => l.id === survey.defaultLanguageId,
            );
            return matchingLanguage && lang.code === matchingLanguage.code;
          })
        : null;
      setSelectedLanguage(defaultLang?.value || surveyLanguages[0].value);
    }
  }, [isOpen, surveyLanguages, survey?.defaultLanguageId, survey?.languages, selectedLanguage]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed left-[255px] top-[72px] right-0 bottom-0 z-40 flex items-center justify-center"
      style={{
        background:
          'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
      }}
    >
      <div className="bg-[#F2EBEE] rounded-lg shadow-xl w-[800px] max-h-[85vh] flex flex-col relative">
        <div className="flex justify-between items-center mx-4 mt-3 mb-1">
          <h2 className="text-lg font-bold text-[#851855]">Preview - Survey Questions</h2>
          <button
            onClick={() => {
              onClose();
              setSelectedLanguage('');
            }}
            className="text-[#851855] hover:text-pink-900"
          >
            <X size={20} />
          </button>
        </div>

        <div className="border-b border-black my-2"></div>

        {surveyLanguages.length > 0 && (
          <div className="px-4">
            <h4 className="text-sm font-bold text-[#621C43] mb-2">Select Language</h4>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 overflow-hidden">
                {surveyLanguages.length > 5 && (
                  <button
                    onClick={() => setCurrentLanguageIndex(Math.max(0, currentLanguageIndex - 1))}
                    disabled={currentLanguageIndex === 0}
                    className="p-1 rounded-full hover:bg-[#4A082D]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={16} className="text-[#621C43]" />
                  </button>
                )}

                <div className="flex overflow-hidden gap-1">
                  {(surveyLanguages.length <= 5
                    ? surveyLanguages
                    : surveyLanguages.slice(currentLanguageIndex, currentLanguageIndex + 5)
                  ).map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => setSelectedLanguage(lang.value)}
                      className={`px-2 py-1.5 text-xs font-medium italic transition-all whitespace-nowrap rounded-lg ${
                        selectedLanguage === lang.value
                          ? 'bg-[#851855] text-white'
                          : 'bg-[#4A082D33] text-[#621C43] hover:bg-[#621C43]/10'
                      }`}
                    >
                      {lang.name} ({lang.code})
                    </button>
                  ))}
                </div>

                {surveyLanguages.length > 5 && (
                  <button
                    onClick={() =>
                      setCurrentLanguageIndex(
                        Math.min(surveyLanguages.length - 5, currentLanguageIndex + 1),
                      )
                    }
                    disabled={currentLanguageIndex >= surveyLanguages.length - 5}
                    className="p-1 rounded-full hover:bg-[#4A082D]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={16} className="text-[#621C43]" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mx-4 my-2">
          <h3 className="text-md text-[#2B1C25] mb-3">{survey?.title}</h3>
          <div className="relative bg-[#FCF3F7] rounded-md pl-5">
            <CustomScrollbar maxHeight="max-h-[60vh]">
              {selectedLanguage ? (
                survey?.questionsJson?.length ? (
                  <QuestionRenderer
                    questions={survey.questionsJson}
                    selectedLanguage={selectedLanguage}
                    disabled={false}
                  />
                ) : (
                  <div className="text-center py-8">
                    <FileText className="mx-auto text-gray-400" size={48} />
                    <h4 className="text-lg font-medium text-[#621C43] mt-4">
                      No Questions Available
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      The questions from the uploaded ODK file are not available for preview.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
                      <h5 className="font-medium text-yellow-800 mb-2">Possible reasons:</h5>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        <li>• The ODK file hasn't been processed yet</li>
                        <li>• The file format is not supported</li>
                        <li>• The file doesn't contain valid question data</li>
                        <li>• There was an error parsing the file</li>
                      </ul>
                    </div>

                    {survey?.odkFileUrl && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500">
                          ODK File:{' '}
                          {survey.odkFileUrl.split('/').pop()?.split('_').slice(1).join('_') ||
                            'Unknown'}
                        </p>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <p className="text-sm text-gray-500">Please select a language to view questions.</p>
              )}
            </CustomScrollbar>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyPreviewModal;
